import * as THREE from 'three';
import { INSTRUMENTS } from '../core/SimConfig.js';
import { AXIS, DEG2RAD, RAD2DEG } from '../utils.js';

const MS_TO_KNOTS = 1.94384;
const M_TO_FEET = 3.28084;

const _euler = new THREE.Euler(0, 0, 0, 'YXZ');
const _q = new THREE.Quaternion();
const _qPitch = new THREE.Quaternion();
const _qRoll = new THREE.Quaternion();
const _ballTarget = { pitch: 0, roll: 0 };

/** `angle` shifted by a multiple of 2π to lie within π of `ref`. */
function unwrapNear(angle, ref) {
  return angle + Math.round((ref - angle) / (2 * Math.PI)) * 2 * Math.PI;
}

const NEEDLE_KEYS = ['airspeed', 'airspeedMax', 'altitude', 'aoa', 'climb', 'flaps', 'gmeter', 'headingCompass', 'altHundred', 'altThousand'];

/**
 * Animates the physical cockpit instrument meshes (needles, attitude ball,
 * gear indicator) from aircraft state. Uses a spring-damper per axis for a
 * "bouncy" feel. Only ticks while the cockpit camera is active.
 */
export default class CockpitInstruments {
  constructor({ aircraft, model, cameraManager }) {
    this.aircraft = aircraft;
    this.cameraManager = cameraManager;

    // ── Rotary needles ──────────────────────────────────────────────────
    this.needles = [];
    for (const key of NEEDLE_KEYS) {
      const cfg = INSTRUMENTS[key];
      const mesh = model.getObjectByName(cfg.mesh);
      if (!mesh) {
        console.warn(`CockpitInstruments: mesh "${cfg.mesh}" not found`);
        continue;
      }
      // Preserve rest pose across scenario reloads (same model object reused)
      if (!mesh.userData._originalQuat) {
        mesh.userData._originalQuat = mesh.quaternion.clone();
      }
      const baseQuat = mesh.userData._originalQuat.clone();
      mesh.quaternion.copy(baseQuat);

      this.needles.push({
        name: key,
        cfg,
        mesh,
        baseQuat,
        angle: 0,
        angVel: 0,
      });
    }

    // Snap each needle to its current target at construction (no startup bounce)
    for (const n of this.needles) {
      n.angle = this._targetAngle(n);
    }

    // ── Attitude ball ───────────────────────────────────────────────────
    const bCfg = INSTRUMENTS.attitudeBall;
    const ballMesh = model.getObjectByName(bCfg.mesh);
    if (ballMesh) {
      if (!ballMesh.userData._originalQuat) {
        ballMesh.userData._originalQuat = ballMesh.quaternion.clone();
      }
      const baseQuat = ballMesh.userData._originalQuat.clone();
      ballMesh.quaternion.copy(baseQuat);

      _euler.setFromQuaternion(aircraft.quaternion, 'YXZ');
      this.ball = {
        cfg: bCfg,
        mesh: ballMesh,
        baseQuat,
        pitch: _euler.x + (bCfg.pitchOffsetDeg ?? 0) * DEG2RAD,
        pitchVel: 0,
        roll: _euler.z,
        rollVel: 0,
      };
    } else {
      console.warn(`CockpitInstruments: mesh "${bCfg.mesh}" not found`);
      this.ball = null;
    }

    // ── Gear "down and locked" indicator ────────────────────────────────
    const gCfg = INSTRUMENTS.gear;
    this.gearMesh = model.getObjectByName(gCfg.mesh);
    if (!this.gearMesh) {
      console.warn(`CockpitInstruments: mesh "${gCfg.mesh}" not found`);
    }
  }

  _targetValue(name) {
    const a = this.aircraft;
    switch (name) {
      case 'airspeed':    return a.airspeed * MS_TO_KNOTS;
      case 'airspeedMax': {
        // Lerp between clean Vne and dirty limit by actual flap/gear extension
        // (whichever is greater), so the needle tracks the animation smoothly.
        const cfg = INSTRUMENTS.airspeedMax;
        const t = Math.min(1, Math.max(a.flaps ?? 0, a.gear ?? 0));
        return cfg.maxKts + (cfg.maxKtsDirty - cfg.maxKts) * t;
      }
      case 'altitude':
      case 'altHundred':
      case 'altThousand': return a.altitude * M_TO_FEET;
      case 'aoa':         return a.angleOfAttack * RAD2DEG + (INSTRUMENTS.aoa.offsetDeg ?? 0);
      case 'climb':       return a.verticalSpeed * M_TO_FEET * 60;
      case 'flaps':       return (a.flaps ?? 0) * INSTRUMENTS.flaps.fullDeployDeg;
      // Normal (spine) axis only — the same component the HUD's G readout uses.
      case 'gmeter':      return a.gForceLocal.y;
      case 'headingCompass': return this._headingDeg();
    }
    return 0;
  }

  _targetAngle(n) {
    const cfg = n.cfg;
    const value = this._targetValue(n.name);
    const zero = cfg.zeroAngleDeg * DEG2RAD;
    let rot;
    if (n.name === 'flaps') {
      // Flaps target is already in DEGREES of needle rotation (not a quantity)
      rot = value * DEG2RAD * cfg.direction;
    } else {
      rot = (value / cfg.unitsPerRotation) * 2 * Math.PI * cfg.direction;
    }
    // Optional travel clamp (e.g. VSI limited to ±90° of rotation)
    if (cfg.maxRotationDeg != null) {
      const limit = (cfg.maxRotationDeg * DEG2RAD) * 0.5;
      rot = Math.max(-limit, Math.min(limit, rot));
    }
    return zero + rot;
  }

  /**
   * Ball pitch/roll target, continuous with the ball's current state.
   * A YXZ decomposition clamps pitch to ±90° and wraps roll at ±180°, so
   * read raw it jumps 360° in roll when inverted and 180° in roll going over
   * the top, and the spring chases each jump the long way round. The same
   * yaw-free attitude is also (180° − pitch, roll + 180°) — a heading flip
   * the ball can't show — so pick whichever pair, unwrapped by 2π, lies
   * nearest to where the ball already is. A loop then rolls the pitch ladder
   * continuously through 360°, as a real gimballed ADI does.
   * Writes into the module-level `_ballTarget` (pitch excludes the offset).
   */
  _ballTarget(b) {
    _euler.setFromQuaternion(this.aircraft.quaternion, 'YXZ');
    const curPitch = b.pitch - (b.cfg.pitchOffsetDeg ?? 0) * DEG2RAD;
    const curRoll = b.roll;
    const pA = unwrapNear(_euler.x, curPitch);
    const rA = unwrapNear(_euler.z, curRoll);
    const pB = unwrapNear(Math.PI - _euler.x, curPitch);
    const rB = unwrapNear(_euler.z + Math.PI, curRoll);
    const dA = (pA - curPitch) ** 2 + (rA - curRoll) ** 2;
    const dB = (pB - curPitch) ** 2 + (rB - curRoll) ** 2;
    if (dA <= dB) { _ballTarget.pitch = pA; _ballTarget.roll = rA; }
    else          { _ballTarget.pitch = pB; _ballTarget.roll = rB; }
  }

  _headingDeg() {
    // Heading derived from aircraft forward vector (magnetic/true north = -Z).
    // atan2(-fwdX, -fwdZ) → 0° at north, +90° at east (clockwise from above).
    const fwd = this.aircraft.forwardVector;
    const rad = Math.atan2(-fwd.x, -fwd.z);
    let deg = rad * RAD2DEG;
    if (deg < 0) deg += 360;
    return deg;
  }

  update(dt) {
    const inCockpit = !!this.cameraManager?.cockpitView;

    // Out of cockpit: snap state to current targets (no spring catch-up on
    // re-entry) and hide the gear indicator. Skip rotation work entirely.
    if (!inCockpit) {
      for (const n of this.needles) {
        n.angle = this._targetAngle(n);
        n.angVel = 0;
      }
      if (this.ball) {
        this._ballTarget(this.ball);
        this.ball.pitch = _ballTarget.pitch + (this.ball.cfg.pitchOffsetDeg ?? 0) * DEG2RAD;
        this.ball.roll = _ballTarget.roll;
        this.ball.pitchVel = 0;
        this.ball.rollVel = 0;
      }
      if (this.gearMesh) this.gearMesh.visible = false;
      return;
    }

    const kDefault = INSTRUMENTS.stiffness;
    const cDefault = INSTRUMENTS.damping;

    // ── Needles — spring-damper toward target angle ─────────────────────
    for (const n of this.needles) {
      let target = this._targetAngle(n);
      // For continuous (unclamped) dials, shift target by 2π multiples so the
      // needle always takes the short path — fixes compass spinning all the
      // way around when heading crosses 0°/360°.
      if (n.cfg.maxRotationDeg == null) {
        target += Math.round((n.angle - target) / (2 * Math.PI)) * 2 * Math.PI;
      }
      const k = n.cfg.stiffness ?? kDefault;
      const c = n.cfg.damping ?? cDefault;

      // Semi-implicit Euler on a damped harmonic oscillator (unit mass)
      const accel = k * (target - n.angle) - c * n.angVel;
      n.angVel += accel * dt;
      n.angle += n.angVel * dt;

      _q.setFromAxisAngle(AXIS[n.cfg.axis], n.angle);
      n.mesh.quaternion.copy(n.baseQuat).multiply(_q);
    }

    // ── Attitude ball — two-axis spring-damper ──────────────────────────
    if (this.ball) {
      const b = this.ball;
      const k = b.cfg.stiffness ?? kDefault;
      const c = b.cfg.damping ?? cDefault;

      this._ballTarget(b);
      const targetPitch = _ballTarget.pitch + (b.cfg.pitchOffsetDeg ?? 0) * DEG2RAD;
      const targetRoll = _ballTarget.roll;

      const accelP = k * (targetPitch - b.pitch) - c * b.pitchVel;
      const accelR = k * (targetRoll - b.roll) - c * b.rollVel;
      b.pitchVel += accelP * dt;
      b.rollVel += accelR * dt;
      b.pitch += b.pitchVel * dt;
      b.roll += b.rollVel * dt;

      // R_ball = R_roll(direction·roll) * R_pitch(direction·pitch) — inverts
      // the aircraft's YXZ pitch+roll so the ball shows the world horizon.
      _qPitch.setFromAxisAngle(AXIS[b.cfg.pitchAxis], b.pitch * b.cfg.pitchDirection);
      _qRoll.setFromAxisAngle(AXIS[b.cfg.rollAxis], b.roll * b.cfg.rollDirection);
      _q.copy(_qRoll).multiply(_qPitch);
      b.mesh.quaternion.copy(b.baseQuat).multiply(_q);
    }

    // ── Gear down-and-locked indicator ──────────────────────────────────
    if (this.gearMesh) {
      this.gearMesh.visible = this.aircraft.gear >= INSTRUMENTS.gear.threshold;
    }
  }

  dispose() {
    // Restore rest pose so nothing is left mid-spring if the scenario tears
    // down while the cockpit is visible.
    for (const n of this.needles) n.mesh.quaternion.copy(n.baseQuat);
    if (this.ball) this.ball.mesh.quaternion.copy(this.ball.baseQuat);
    if (this.gearMesh) this.gearMesh.visible = true;
  }
}
